// src/components/Relatorios.js
// =============================================
// MENU RELATÓRIOS — Container principal com abas
// ✅ Abas: Vendas | Financeiro | Stock | Clientes | Fornecedores
// ✅ Reutiliza os componentes existentes em ./Relatorios/
// 🔥 CORRIGIDO v5 (definitivo):
//    - Lê o utilizador via GET /api/users/me (perfil + individuais + empresaId)
//    - Lê os módulos ativos da empresa via GET /api/empresas/:id/modulos
//      (esse endpoint JÁ normaliza a estrutura com garantirTodosModulos,
//       por isso nunca vem vazio nem em formato antigo)
//    - A aba Stock só aparece se, ao mesmo tempo:
//        (a) perfil + permissoesIndividuais derem acesso a stock.criar_produto
//        (b) empresa tiver módulo stock.ativo === true
//        (c) empresa tiver submódulo stock.criar_produto === true
//    - Logs claros [Relatorios] para debug
//    - Loading enquanto /me + /modulos não respondem
// =============================================
import React, {
    useState,
    Suspense,
    lazy,
    useEffect,
    useMemo,
    useCallback
} from 'react';
import axios from 'axios';
import { Icon } from './SharedComponents';
import { perfilTemAcessoSubmodulo } from '../utils/menuStructure';
import './Relatorios.css';

const API = '/api';

// =============================================
// 🔥 LAZY LOADING
// =============================================
const RelatorioVendas = lazy(() => import('./RelatorioVendas'));
const RelatorioFinanceiro = lazy(() => import('./RelatorioFinanceiro'));
const RelatorioStock = lazy(() => import('./RelatorioStock'));
const RelatorioClientes = lazy(() => import('./RelatorioClientes'));
const RelatorioFornecedores = lazy(() => import('./RelatorioFornecedores'));

// =============================================
// 🔥 ABAS BASE
// =============================================
const ABAS_BASE = [
    { id: 'vendas', label: 'Vendas', icone: 'receipt_long', cor: '#4f46e5' },
    { id: 'financeiro', label: 'Financeiro', icone: 'account_balance', cor: '#10b981' },
    { id: 'stock', label: 'Stock', icone: 'inventory_2', cor: '#f59e0b' },
    { id: 'clientes', label: 'Clientes', icone: 'people', cor: '#8b5cf6' },
    { id: 'fornecedores', label: 'Fornecedores', icone: 'local_shipping', cor: '#06b6d4' }
];

const LoadingFallback = () => (
    <div className="relatorios-loading">
        <div className="relatorios-spinner"></div>
        <p>A carregar relatório...</p>
    </div>
);

// =============================================
// 🔥 VERIFICAR ACESSO À ABA STOCK
// =============================================
function podeVerAbaStock(user, modulosEmpresa) {
    if (!user) return false;

    const perfil = user.perfil || 'Normal';
    const permissoesIndividuais = user.permissoesIndividuais || {};

    // 1) perfil + individuais (via menuStructure)
    const temPermissaoUser = perfilTemAcessoSubmodulo(
        perfil,
        'stock',
        'criar_produto',
        permissoesIndividuais
    );

    if (!temPermissaoUser) {
        console.log('🔒 [Relatorios] Stock oculta: perfil sem stock.criar_produto', {
            perfil,
            stockIndividual: permissoesIndividuais?.stock
        });
        return false;
    }

    // 2) módulos da empresa (vindos de /api/empresas/:id/modulos)
    const moduloStock = modulosEmpresa?.stock;
    if (!moduloStock) {
        console.log('🔒 [Relatorios] Stock oculta: empresa sem módulo stock', {
            modulosKeys: Object.keys(modulosEmpresa || {})
        });
        return false;
    }

    // Estrutura antiga (booleano) — aceitar
    if (typeof moduloStock === 'boolean') {
        return moduloStock === true;
    }

    // 3) módulo ativo
    if (moduloStock.ativo !== true) {
        console.log('🔒 [Relatorios] Stock oculta: stock.ativo !== true');
        return false;
    }

    // 4) submódulo criar_produto ativo
    const subs = moduloStock.submodulos || {};
    if (subs.criar_produto !== true) {
        console.log('🔒 [Relatorios] Stock oculta: stock.criar_produto !== true', {
            submodulosStock: Object.keys(subs),
            valor: subs.criar_produto
        });
        return false;
    }

    return true;
}

export default function Relatorios() {
    const [abaAtiva, setAbaAtiva] = useState('vendas');
    const [user, setUser] = useState(null);
    const [modulosEmpresa, setModulosEmpresa] = useState(null);
    const [carregando, setCarregando] = useState(true);

    // =============================================
    // 🔥 CARREGAR TUDO
    // =============================================
    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.warn('⚠️ [Relatorios] Sem token');
                setUser(null);
                setModulosEmpresa(null);
                return;
            }

            const headers = { Authorization: `Bearer ${token}` };

            // 1) Utilizador
            const meRes = await axios.get(`${API}/users/me`, { headers });
            if (!meRes.data?.success || !meRes.data?.data) {
                console.warn('⚠️ [Relatorios] /me resposta inesperada');
                setUser(null);
                setModulosEmpresa(null);
                return;
            }

            const u = meRes.data.data;

            // extrair empresaId como string
            let empresaIdStr = null;
            if (u.empresaId) {
                empresaIdStr =
                    typeof u.empresaId === 'object'
                        ? (u.empresaId._id || u.empresaId.id)
                        : u.empresaId;
            }

            const userNormalizado = {
                _id: u._id,
                nome: u.nome,
                email: u.email,
                perfil: u.perfil || 'Normal',
                permissoesIndividuais: u.permissoesIndividuais || {},
                empresaId: empresaIdStr,
                empresa: u.empresa || null
            };

            setUser(userNormalizado);

            // 2) Módulos da empresa (fonte de verdade normalizada)
            if (empresaIdStr) {
                try {
                    const modRes = await axios.get(
                        `${API}/empresas/${empresaIdStr}/modulos`,
                        { headers }
                    );
                    if (modRes.data?.success && modRes.data?.data) {
                        setModulosEmpresa(modRes.data.data);
                        console.log('✅ [Relatorios] módulos empresa:', {
                            stock: modRes.data.data.stock
                        });
                    } else {
                        console.warn('⚠️ [Relatorios] /modulos vazio');
                        setModulosEmpresa({});
                    }
                } catch (errMod) {
                    console.error(
                        '❌ [Relatorios] erro /modulos:',
                        errMod?.response?.data || errMod.message
                    );
                    // Tentar fallback: user.empresaId.modulosAtivos (se /me populou)
                    const fb =
                        (typeof u.empresaId === 'object' && u.empresaId?.modulosAtivos) ||
                        u.modulosAtivos ||
                        {};
                    setModulosEmpresa(fb);
                }
            } else {
                console.warn('⚠️ [Relatorios] user sem empresaId');
                setModulosEmpresa({});
            }

            console.log('✅ [Relatorios] user:', {
                perfil: userNormalizado.perfil,
                temIndividuais:
                    Object.keys(userNormalizado.permissoesIndividuais).length > 0,
                empresaId: userNormalizado.empresaId
            });
        } catch (err) {
            console.error(
                '❌ [Relatorios] erro /me:',
                err?.response?.data || err.message
            );
            setUser(null);
            setModulosEmpresa(null);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    // =============================================
    // 🔥 ABAS VISÍVEIS
    // =============================================
    const abasVisiveis = useMemo(() => {
        return ABAS_BASE.filter(aba => {
            if (aba.id === 'stock') {
                return podeVerAbaStock(user, modulosEmpresa);
            }
            return true;
        });
    }, [user, modulosEmpresa]);

    useEffect(() => {
        const ids = abasVisiveis.map(a => a.id);
        if (!ids.includes(abaAtiva) && ids.length > 0) {
            setAbaAtiva(ids[0]);
        }
    }, [abasVisiveis, abaAtiva]);

    const renderRelatorio = () => {
        switch (abaAtiva) {
            case 'vendas': return <RelatorioVendas />;
            case 'financeiro': return <RelatorioFinanceiro />;
            case 'stock': return <RelatorioStock />;
            case 'clientes': return <RelatorioClientes />;
            case 'fornecedores': return <RelatorioFornecedores />;
            default: return <RelatorioVendas />;
        }
    };

    if (carregando) {
        return (
            <div className="relatorios-wrapper">
                <div className="relatorios-header">
                    <div className="relatorios-title">
                        <div className="relatorios-title-icon">
                            <Icon name="bar_chart" size={26} />
                        </div>
                        <div>
                            <h1>Relatórios</h1>
                            <p>Análise detalhada • Exportação • Auditoria</p>
                        </div>
                    </div>
                </div>
                <div className="relatorios-loading">
                    <div className="relatorios-spinner"></div>
                    <p>A carregar permissões...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relatorios-wrapper">
            <div className="relatorios-header">
                <div className="relatorios-title">
                    <div className="relatorios-title-icon">
                        <Icon name="bar_chart" size={26} />
                    </div>
                    <div>
                        <h1>Relatórios</h1>
                        <p>Análise detalhada • Exportação • Auditoria</p>
                    </div>
                </div>
            </div>

            <nav className="relatorios-abas">
                {abasVisiveis.map(aba => (
                    <button
                        key={aba.id}
                        className={`relatorios-aba-btn ${abaAtiva === aba.id ? 'ativa' : ''}`}
                        onClick={() => setAbaAtiva(aba.id)}
                        style={{ '--aba-cor': aba.cor }}
                    >
                        <span className="relatorios-aba-icone">
                            <Icon
                                name={aba.icone}
                                size={18}
                                color={abaAtiva === aba.id ? '#fff' : aba.cor}
                            />
                        </span>
                        <span className="relatorios-aba-label">{aba.label}</span>
                    </button>
                ))}
            </nav>

            <div className="relatorios-conteudo">
                <Suspense fallback={<LoadingFallback />}>
                    {renderRelatorio()}
                </Suspense>
            </div>
        </div>
    );
}